"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Crown, Shield, Lightbulb, Search, Eye } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LErmitePage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-yellow-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-yellow-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-yellow-500">/</span>
            <span className="text-sm">Major Arkana</span>
            <span className="text-yellow-500">/</span>
            <span className="text-sm">L'Ermite</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Major Arkana'ya Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-amber-900/20 to-yellow-900/20 rounded-xl p-8 mb-8 border border-yellow-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/marseille-l-ermite.svg"
                  alt="L'Ermite Tarot Card"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gradient-to-b from-amber-900/40 to-yellow-900/40 border border-yellow-500/30 flex-col items-center justify-center text-yellow-300">
                  <span className="text-3xl font-bold mb-2">IX</span>
                  <span className="text-lg font-semibold">L'ERMITE</span>
                  <span className="text-sm text-center mt-2">The Hermit</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">IX</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">L'Ermite</h1>
                  <p className="text-xl text-yellow-400">Ermit / İç Yolculuk ve Bilgelik</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde dokuzuncu numara. Fenerini yükseltmiş ermit, iç arayış,
                tefekkür ve manevi rehberlik temsil eder. Yalnızlıkta bilgelik arama kartıdır.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm">İç Arayış</span>
                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm">Tefekkür</span>
                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm">Bilgelik</span>
                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm">Rehberlik</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Lightbulb className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İç Bilgelik</h3>
                  <p className="text-slate-400 text-sm">Ruhsal anlayış, iç ses dinleme ve sezgisel bilgi.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Ruhsal Rehberlik</h3>
                  <p className="text-slate-400 text-sm">Mentor olma, başkalarını aydınlatma ve yol gösterme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Derin Düşünce</h3>
                  <p className="text-slate-400 text-sm">Tefekkür, meditasyon ve kendinle yüzleşme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Maneviyat Arayışı</h3>
                  <p className="text-slate-400 text-sm">Ruhsal yolculuk, anlam arama ve iç huzur bulma.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Shield className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Aşırı İzolasyon</h3>
                  <p className="text-slate-400 text-sm">Toplumdan kopma, sosyal bağlardan uzaklaşmak.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kararsızlık</h3>
                  <p className="text-slate-400 text-sm">İç karmaşıklık, yönünü bulamama ve tereddüt.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yanlış Rehberlik</h3>
                  <p className="text-slate-400 text-sm">Yoldan çıkma, dogmatik düşünce ve sapmalar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Manevi Kayıp</h3>
                  <p className="text-slate-400 text-sm">Anlam yokluğu, ruhsal boşluk ve umutsuzluk.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Lightbulb className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Fener</h3>
              <p className="text-slate-400 text-sm">İç ışık, rehberlik ve bilgelik aydınlatması.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🦯</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Asa</h3>
              <p className="text-slate-400 text-sm">Destek, yolculuk ve yaşam tecrübesi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🎭</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kapüşon</h3>
              <p className="text-slate-400 text-sm">Gizlilik, iç dünya ve manevi koruma.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Search className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yalnız Yolculuk</h3>
              <p className="text-slate-400 text-sm">Kendi başına arayış, bireyselleştirme.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Eye className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">İç Görüş</h3>
              <p className="text-slate-400 text-sm">Sezgi, iç bilgi ve ruhsal algı.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">♍</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Başak Enerjisi</h3>
              <p className="text-slate-400 text-sm">Analiz, ayrıntı ve mükemmeliyetçilik.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Kendi kendine zaman</li>
                <li>• Ruhsal bağlılık</li>
                <li>• Derin anlaşma</li>
                <li>• Manevi rehberlik</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Danışmanlık</li>
                <li>• Eğitmenlik</li>
                <li>• Araştırma ve geliştirme</li>
                <li>• Psikoloji ve terapi</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İç keşfetme</li>
                <li>• Bilgelik kazanma</li>
                <li>• Sabır geliştirme</li>
                <li>• Manevi olgunluk</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-400" />
                Manevi Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Meditasyon pratiği</li>
                <li>• Ruhsal arayış</li>
                <li>• İç huzur bulma</li>
                <li>• Evrensel bilgelik</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana/la-justice"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: La Justice
          </Link>

          <Link
            href="/about/tarot/systems/marseille/major-arcana/la-roue-de-fortune"
            className="inline-flex items-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
          >
            Sonraki: La Roue de Fortune
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}